// @/components/reusable/notification-banner.jsx

/**
 * NotificationBanner — Reusable dismissible banner for contextual messages.
 *
 * Follows the v2 card-style design language:
 *   • Rounded-xl with subtle border + translucent background
 *   • Hover state colour shift
 *   • Framer-Motion entrance / exit
 *   • Optional dismiss button with circled bg (persists to sessionStorage when `storageKey` given)
 *   • Optional full-box click (href or onClick) with hover-reveal arrow (when not dismissible)
 *   • Text rendered as h4 with flex-col, font-medium, text-sm/5, variant colour
 *
 * Variants: warning (orange), primary (blue), info (zinc), error (red), neutral (zinc)
 *
 * @module components/reusable/notification-banner
 */

import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { INTRO, EXIT } from './animations';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { HoverTooltip } from '@/components/reusable/hover-tooltip';

// ─── colour maps ───────────────────────────────────────────────────
const VARIANT_STYLES = {
  warning: {
    wrapper: 'border-orange-100 bg-orange-50/50 hover:bg-orange-50',
    text: 'text-orange-600',
    close: 'text-orange-600 hover:text-orange-800 focus:ring-orange-400',
    closeBg: 'bg-orange-100/50 hover:bg-orange-100',
  },
  primary: {
    wrapper: 'border-blue-100 bg-blue-50/50 hover:bg-blue-50',
    text: 'text-primary',
    close: 'text-primary hover:text-blue-800 focus:ring-blue-400',
    closeBg: 'bg-blue-100/50 hover:bg-blue-100',
  },
  info: {
    wrapper: 'border-zinc-200 bg-zinc-50/50 hover:bg-zinc-100',
    text: 'text-zinc-600',
    close: 'text-zinc-400 hover:text-zinc-600 focus:ring-zinc-400',
    closeBg: 'bg-zinc-100/50 hover:bg-zinc-100',
  },
  success: {
    wrapper: 'border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50',
    text: 'text-teal-700',
    close: 'text-teal-500 hover:text-teal-700 focus:ring-teal-400',
    closeBg: 'bg-emerald-100/50 hover:bg-emerald-100',
  },
  error: {
    wrapper: 'border-red-200 bg-red-50/50 hover:bg-red-50',
    text: 'text-red-800',
    close: 'text-red-500 hover:text-red-700 focus:ring-red-500',
    closeBg: 'bg-red-100/50 hover:bg-red-100',
  },
  neutral: {
    wrapper: 'border-zinc-200 bg-zinc-50/50 hover:bg-zinc-100',
    text: 'text-zinc-700',
    close: 'text-zinc-400 hover:text-zinc-600 focus:ring-zinc-400',
    closeBg: 'bg-zinc-100/50 hover:bg-zinc-100',
  },
};

/**
 * @param {Object}  props
 * @param {React.ReactNode}  props.children     — Banner content (text, links, etc.)
 * @param {'warning'|'primary'|'info'|'error'|'neutral'} [props.variant='warning']
 * @param {boolean} [props.dismissible=true]    — Show close button
 * @param {string}  [props.storageKey]          — sessionStorage key; when set the
 *                                                dismissed state survives page navigations
 * @param {Function} [props.onDismiss]          — Optional callback fired on dismiss
 * @param {React.ReactNode} [props.dismissTooltip] — When set, the dismiss X reveals this
 *                                                on hover/focus, explaining what closing does
 * @param {React.ReactNode} [props.action]      — Optional control rendered in the top-right
 *                                                slot (where the dismiss X sits)
 * @param {string}  [props.href]               — URL; entire banner becomes a clickable link
 * @param {Function} [props.onClick]           — Click handler; entire banner becomes clickable
 * @param {React.ReactNode} [props.icon]       — Optional icon element rendered left of content
 * @param {string}  [props.className]           — Extra classes on outer wrapper
 */
const NotificationBanner = ({
  children,
  variant = 'warning',
  dismissible = true,
  storageKey,
  onDismiss,
  dismissTooltip,
  action,
  href,
  onClick,
  icon,
  className = '',
}) => {
  // Restore dismissed state from sessionStorage when a key is provided
  const [isDismissed, setIsDismissed] = useState(() => {
    if (!storageKey || typeof window === 'undefined') return false;
    return sessionStorage.getItem(storageKey) === '1';
  });

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
    if (storageKey && typeof window !== 'undefined') {
      sessionStorage.setItem(storageKey, '1');
    }
    onDismiss?.();
  }, [storageKey, onDismiss]);

  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.warning;
  const isClickable = !!(href || onClick);

  const handleClick = isClickable
    ? (e) => {
      if (onClick) { onClick(e); return; }
      if (href) window.location.href = href;
    }
    : undefined;

  const handleKeyDown = isClickable
    ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(e); }
    : undefined;

  return (
    <AnimatePresence>
      {!isDismissed && (
        <motion.div
          {...INTRO}
          exit={EXIT}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          role={isClickable ? 'link' : undefined}
          tabIndex={isClickable ? 0 : undefined}
          className={`rounded-xl border transition-colors ${isClickable ? 'group cursor-pointer' : ''} ${styles.wrapper} ${className}`}
        >
          <div className="flex items-center justify-between gap-3 px-8 py-6">
            {icon && <div className="flex-none">{icon}</div>}
            <h4 className={`flex-1 flex flex-col font-medium text-sm/5 ${styles.text}`}>
              {children}
            </h4>

            {isClickable && !dismissible && !action && (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={`size-5 shrink-0 opacity-0 -translate-x-1 transition-all duration-150 group-hover:opacity-100 group-hover:translate-x-0 ${styles.text}`} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
              </svg>
            )}

            {action && <div className="flex-none">{action}</div>}

            {dismissible && (() => {
              const dismissBtn = (
                <button
                  type="button"
                  className={`shrink-0 rounded-full p-1.5 cursor-pointer transition-colors ${styles.closeBg} ${styles.close}`}
                  onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
                  aria-label="Dismiss"
                >
                  <XMarkIcon className="size-4" aria-hidden="true" />
                </button>
              );
              return dismissTooltip
                ? <HoverTooltip content={dismissTooltip}>{dismissBtn}</HoverTooltip>
                : dismissBtn;
            })()}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { NotificationBanner };
